import type { createFormatter } from "next-intl"

type Formatter = ReturnType<typeof createFormatter>
type DateTimeOptions = NonNullable<Parameters<Formatter["dateTime"]>[2]>

const DATE_PATTERNS = {
  "MM/DD/YYYY": ["MM", "DD", "YYYY"],
  "DD/MM/YYYY": ["DD", "MM", "YYYY"],
  "YYYY-MM-DD": ["YYYY", "MM", "DD"],
  "DD.MM.YYYY": ["DD", "MM", "YYYY"],
} as const

export interface ClinicDateTimePreferences {
  dateFormat?: keyof typeof DATE_PATTERNS
  timeFormat?: "12h" | "24h"
}

export function clinicDateTimePreferences(
  settings?: Record<string, unknown>,
): ClinicDateTimePreferences {
  const date = settings?.date_format
  const time = settings?.time_format
  return {
    dateFormat: typeof date === "string" && Object.hasOwn(DATE_PATTERNS, date)
      ? date as ClinicDateTimePreferences["dateFormat"]
      : undefined,
    timeFormat: time === "12h" || time === "24h" ? time : undefined,
  }
}

/** Format an API wall-clock value without moving it to another time zone. */
export function formatClockTime(formatter: Pick<Formatter, "dateTime">, value: string): string {
  if (!/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value)) return value
  return formatter.dateTime(new Date(`2000-01-01T${value.length === 5 ? `${value}:00` : value}Z`), {
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "UTC",
  })
}

/** Apply clinic display preferences while retaining localized names and the caller's time zone. */
export function withClinicDateTime(
  formatter: Formatter,
  preferences: ClinicDateTimePreferences,
  timeZone?: string,
): Formatter {
  const { dateFormat, timeFormat } = preferences
  if (!dateFormat && !timeFormat) return formatter

  const datePartFormatters = new Map<string | undefined, Intl.DateTimeFormat>()

  const dateTime = (
    value: Date | number,
    formatOrOptions?: string | DateTimeOptions,
    overrides?: DateTimeOptions,
  ): string => {
    // Named formats retain next-intl's lookup and error handling.
    if (typeof formatOrOptions === "string") {
      return formatter.dateTime(value, formatOrOptions, overrides)
    }
    const options = { ...formatOrOptions, ...overrides }
    if (Number.isNaN(new Date(value).getTime())) return formatter.dateTime(value, options)

    const hasTime = !!(options.hour || options.minute || options.second || options.timeStyle)
    if (timeFormat && hasTime) {
      // h23 keeps midnight at 00:00; hour12:false can select 24:00 in English.
      delete options.hour12
      delete options.dayPeriod
      options.hourCycle = timeFormat === "24h" ? "h23" : "h12"
      if (options.hour) options.hour = timeFormat === "24h" ? "2-digit" : "numeric"
    }

    const defaultDate = !hasTime && !options.year && !options.month && !options.day && !options.weekday && !options.era
    const hasDate = options.dateStyle || defaultDate || (options.month && options.day)
    if (!dateFormat || !hasDate) return formatter.dateTime(value, options)

    const zone = options.timeZone ?? timeZone
    let partsFormatter = datePartFormatters.get(zone)
    if (!partsFormatter) {
      partsFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
      })
      datePartFormatters.set(zone, partsFormatter)
    }
    const parts = partsFormatter.formatToParts(value)
    const tokens = {
      YYYY: parts.find((part) => part.type === "year")!.value,
      MM: parts.find((part) => part.type === "month")!.value,
      DD: parts.find((part) => part.type === "day")!.value,
    }
    const includeYear = !!(options.year || options.dateStyle || defaultDate)
    const separator = dateFormat.includes("/") ? "/" : dateFormat.includes(".") ? "." : "-"
    const date = DATE_PATTERNS[dateFormat]
      .filter((token) => token !== "YYYY" || includeYear)
      .map((token) => tokens[token])
      .join(separator)
    const labels = []
    const weekday = options.weekday ?? (options.dateStyle === "full" ? "long" : undefined)
    if (weekday) labels.push(formatter.dateTime(value, { weekday, timeZone: zone }))
    labels.push(date)

    if (hasTime) {
      const clockOptions = { ...options }
      delete clockOptions.dateStyle
      delete clockOptions.year
      delete clockOptions.month
      delete clockOptions.day
      delete clockOptions.weekday
      delete clockOptions.era
      labels.push(formatter.dateTime(value, clockOptions))
    }
    return labels.join(", ")
  }

  const dateTimeRange = (
    start: Date | number,
    end: Date | number,
    formatOrOptions?: string | DateTimeOptions,
    overrides?: DateTimeOptions,
  ): string => {
    if (typeof formatOrOptions === "string") {
      return formatter.dateTimeRange(start, end, formatOrOptions, overrides)
    }
    const first = dateTime(start, formatOrOptions, overrides)
    const last = dateTime(end, formatOrOptions, overrides)
    return first === last ? first : `${first} – ${last}`
  }

  return { ...formatter, dateTime, dateTimeRange }
}
